/**
 * Equipment Analytics — measured utilisation from the equipment register,
 * with a derived equipment-hour demand overlay from the F&C schedule.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Area, Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Loader2, Layers, Truck, AlertTriangle, Info } from 'lucide-react';
import api from '@/lib/api';
import { usePanelTheme } from './panelTheme';
import ModuleDrilldownDrawer from './ModuleDrilldownDrawer';

const SCALES = [['day', 'Daily'], ['week', 'Weekly'], ['month', 'Monthly']];

const inr = (v) => {
  if (!v && v !== 0) return '—';
  const a = Math.abs(v); const s = v < 0 ? '-' : '';
  if (a >= 1e7) return `${s}₹${(a / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `${s}₹${(a / 1e5).toFixed(2)} L`;
  return `${s}₹${Math.round(a).toLocaleString('en-IN')}`;
};
const hrs = (v) => `${Math.round(v || 0).toLocaleString('en-IN')} h`;

const Pick = ({ value, onChange, options, placeholder, testId, T }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className="text-[10px] rounded-lg px-2 py-1 outline-none max-w-[150px]"
    style={{ background: T.inner, border: `1px solid ${T.border}`, color: T.text }}>
    <option value="">{placeholder}</option>
    {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const BarRows = ({ label, rows, valueOf, fmt, color, T, testId }) => {
  const max = Math.max(1, ...(rows || []).map((r) => valueOf(r) || 0));
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: T.muted }}>{label}</div>
      <div className="space-y-1.5">
        {(rows || []).slice(0, 6).map((r) => (
          <div key={r.name}>
            <div className="flex justify-between text-[10px] mb-0.5">
              <span className="truncate pr-2" style={{ color: T.text2 }}>{r.name}</span>
              <span className="font-mono" style={{ color: T.muted }}>{fmt(valueOf(r), r)}</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: T.inner }}>
              <div className="h-full rounded-full transition-all duration-700"
                   style={{ width: `${((valueOf(r) || 0) / max) * 100}%`, background: color }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Watchlist = ({ rows, T }) => (
  <div data-testid="equipment-watchlist">
    <div className="flex items-center gap-1.5 mb-1.5">
      <AlertTriangle className="w-3 h-3" style={{ color: T.warn }} />
      <span className="text-[10px] uppercase tracking-wider" style={{ color: T.muted }}>
        Idle &amp; breakdown watchlist
      </span>
    </div>
    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${T.border}` }}>
      <div className="grid grid-cols-12 px-3 py-1.5 text-[9px] uppercase tracking-wider"
           style={{ background: T.inner, color: T.muted }}>
        <div className="col-span-4">Asset</div>
        <div className="col-span-2">Category</div>
        <div className="col-span-2 text-right">Util %</div>
        <div className="col-span-2 text-right">Lost %</div>
        <div className="col-span-2 text-right">Cost var</div>
      </div>
      {(rows || []).slice(0, 8).map((r) => (
        <div key={r.code} className="grid grid-cols-12 px-3 py-1.5 text-[10px] items-center"
             style={{ borderTop: `1px solid ${T.border}` }} data-testid={`equipment-watch-${r.code}`}>
          <div className="col-span-4 min-w-0">
            <div className="truncate" style={{ color: T.text }}>{r.name}</div>
            <div className="font-mono text-[9px] truncate" style={{ color: T.muted }}>
              {r.code} · {r.vendor} · {String(r.ownership || '').replace('_', ' ').toLowerCase()}
            </div>
          </div>
          <div className="col-span-2 font-mono truncate" style={{ color: T.text2 }}>{r.category}</div>
          <div className="col-span-2 text-right font-mono"
               style={{ color: r.utilisationPct >= 75 ? T.good : r.utilisationPct >= 55 ? T.warn : T.bad }}>
            {r.utilisationPct}
          </div>
          <div className="col-span-2 text-right font-mono" style={{ color: r.lostPct >= 35 ? T.bad : T.text2 }}>
            {r.lostPct}
          </div>
          <div className="col-span-2 text-right font-mono" style={{ color: r.costVariance < 0 ? T.bad : T.good }}>
            {inr(r.costVariance)}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const EquipmentAnalytics = ({ projectId }) => {
  const T = usePanelTheme();
  const [interval, setScale] = useState('month');
  const [category, setCategory] = useState('');
  const [vendor, setVendor] = useState('');
  const [ownership, setOwnership] = useState('');
  const [contractor, setContractor] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    setLoading(true);
    const qs = new URLSearchParams({ interval });
    if (category) qs.set('category', category);
    if (vendor) qs.set('vendor', vendor);
    if (ownership) qs.set('ownership', ownership);
    if (contractor) qs.set('contractor', contractor);
    api.get(`/v1/projects/${projectId}/overview/equipment-analytics?${qs.toString()}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId, interval, category, vendor, ownership, contractor]);

  const s = data?.summary || {};
  const f = data?.filters || {};
  const series = useMemo(() => (data?.series || []).map((p) => ({
    period: p.period,
    Operating: p.operatingHours,
    Idle: p.idleHours,
    Breakdown: p.breakdownHours,
    'Derived demand': p.demandHours,
    'Cum equip-days': p.cumEquipDays,
    Deployed: p.deployedUnits,
  })), [data]);

  const stats = [
    ['Fleet', s.fleetSize ?? 0, T.accent],
    ['Peak deployed', s.peakDeployed ?? 0, T.violet],
    ['Operating', hrs(s.operatingHours), T.good],
    ['Equip-days', Math.round(s.equipDays ?? 0).toLocaleString('en-IN'), T.teal],
    ['Utilisation', `${s.utilisationPct ?? 0}%`, (s.utilisationPct ?? 0) >= 75 ? T.good : T.bad],
    ['Idle', `${s.idlePct ?? 0}%`, (s.idlePct ?? 0) > 25 ? T.bad : T.warn],
    ['Breakdown', `${s.breakdownPct ?? 0}%`, (s.breakdownPct ?? 0) > 5 ? T.bad : T.good],
    ['Cost variance', inr(s.costVariance), (s.costVariance ?? 0) < 0 ? T.bad : T.good],
  ];

  return (
    <div className="rounded-2xl overflow-hidden" data-testid="equipment-analytics-card"
         style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <div className="px-5 pt-4 pb-3 flex flex-wrap items-center gap-2"
           style={{ background: T.sheen, borderBottom: `1px solid ${T.border}` }}>
        <span className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: `${T.warn}1F` }}>
          <Truck className="w-3.5 h-3.5" style={{ color: T.warn }} />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold leading-tight" style={{ color: T.text }}>
            Equipment Analytics — Utilisation, Deployment &amp; Cost
          </h3>
          <p className="text-[10px]" style={{ color: T.muted }}>
            {s.fleetSize ?? 0} assets · {s.categories ?? 0} categories · {series.length} {interval} periods ·
            demand derived from {s.activityCount ?? 0} F&amp;C activities
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <Pick T={T} value={category} onChange={setCategory} options={f.categories}
                placeholder="All categories" testId="equipment-filter-category" />
          <Pick T={T} value={vendor} onChange={setVendor} options={f.vendors}
                placeholder="All vendors" testId="equipment-filter-vendor" />
          <Pick T={T} value={ownership} onChange={setOwnership} options={f.ownerships}
                placeholder="All ownership" testId="equipment-filter-ownership" />
          <Pick T={T} value={contractor} onChange={setContractor} options={f.contractors}
                placeholder="All contractors" testId="equipment-filter-contractor" />
          <button type="button" onClick={() => setDrill(true)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
            style={{ background: `${T.warn}18`, color: T.warn, border: `1px solid ${T.warn}44` }}
            data-testid="equipment-drilldown-btn">
            <Layers className="w-3 h-3" /> L2 · L3
          </button>
          <div className="flex items-center gap-1" data-testid="equipment-scale-toggle">
            {SCALES.map(([key, label]) => (
              <button key={key} type="button" onClick={() => setScale(key)}
                className="text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors"
                style={{ background: interval === key ? T.accent : 'transparent',
                         border: `1px solid ${interval === key ? T.accent : T.border}`,
                         color: interval === key ? '#fff' : T.muted }}
                data-testid={`equipment-scale-${key}`}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="equipment-stats">
          {stats.map(([label, value, tone]) => (
            <div key={label} className="rounded-xl px-2.5 py-2"
                 style={{ background: T.inner, border: `1px solid ${T.border}` }}>
              <div className="text-[9px] uppercase tracking-wider" style={{ color: T.muted }}>{label}</div>
              <div className="text-sm font-mono font-semibold truncate" style={{ color: tone }}>{value}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center gap-2 text-xs" style={{ color: T.muted }}
               data-testid="equipment-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Building equipment curve…
          </div>
        ) : series.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-xs text-center px-8" style={{ color: T.muted }}
               data-testid="equipment-empty">
            No equipment utilisation has been recorded for this filter yet — add assets to the equipment
            register, or clear the category / vendor / ownership filters.
          </div>
        ) : (
          <div className="h-72" data-testid="equipment-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 6, right: 8, left: -14, bottom: 0 }}>
                <defs>
                  <linearGradient id="eqOp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={T.good} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={T.good} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} />
                <XAxis dataKey="period" stroke={T.muted} fontSize={9} angle={-35} textAnchor="end" height={48}
                       interval="preserveStartEnd" minTickGap={interval === 'day' ? 22 : 6} />
                <YAxis yAxisId="left" stroke={T.muted} fontSize={10} />
                <YAxis yAxisId="right" orientation="right" stroke={T.muted} fontSize={10} />
                <Tooltip contentStyle={{ background: T.card, border: `1px solid ${T.border}`, fontSize: 11, color: T.text }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Area yAxisId="left" type="monotone" dataKey="Operating" stroke={T.good} fill="url(#eqOp)" strokeWidth={2} />
                <Bar yAxisId="left" dataKey="Idle" fill={T.warn} fillOpacity={0.55} stackId="lost" barSize={10} />
                <Bar yAxisId="left" dataKey="Breakdown" fill={T.bad} fillOpacity={0.75} stackId="lost" barSize={10} />
                <Line yAxisId="left" type="monotone" dataKey="Derived demand" stroke={T.violet}
                      strokeDasharray="5 4" strokeWidth={1.6} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="Cum equip-days" stroke={T.accent} strokeWidth={1.6} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="flex items-start gap-1.5 text-[9.5px] leading-snug" style={{ color: T.muted }}
             data-testid="equipment-derived-note">
          <Info className="w-3 h-3 mt-[1px] shrink-0" />
          <span>{data?.derivedNote}</span>
        </div>

        {(data?.byCategory || []).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
            <BarRows T={T} testId="equipment-by-category" label="Operating hours by category"
                     rows={data.byCategory} color={T.good}
                     valueOf={(r) => r.operatingHours}
                     fmt={(v, r) => `${hrs(v)} · ${r.utilisationPct}%`} />
            <BarRows T={T} testId="equipment-by-vendor" label="Operating hours by vendor"
                     rows={data.byVendor} color={T.accent}
                     valueOf={(r) => r.operatingHours}
                     fmt={(v, r) => `${hrs(v)} · ${r.assets} assets`} />
            <BarRows T={T} testId="equipment-cost-by-category" label="Actual cost by category"
                     rows={data.byCategory} color={T.violet}
                     valueOf={(r) => r.actualCost}
                     fmt={(v, r) => `${inr(v)} vs ${inr(r.plannedCost)}`} />
          </div>
        )}

        {(data?.byDiscipline || []).length > 0 && (
          <div className="pt-1">
            <BarRows T={T} testId="equipment-by-discipline"
                     label={`Derived equipment demand by F&C discipline${data?.loggedNote ? ' · no hours logged yet' : ''}`}
                     rows={data.byDiscipline} color={T.teal}
                     valueOf={(r) => r.demandHours}
                     fmt={(v, r) => `${hrs(v)} demand · ${hrs(r.loggedHours)} logged`} />
          </div>
        )}

        {(data?.watchlist || []).length > 0 && <Watchlist rows={data.watchlist} T={T} />}
      </div>

      {drill && <ModuleDrilldownDrawer projectId={projectId} module="equipment" onClose={() => setDrill(false)} />}
    </div>
  );
};

export default EquipmentAnalytics;
